// react
import { useEffect, useMemo, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/stock';
import 'highcharts/modules/no-data-to-display';
import { DateTime } from 'luxon';

type ChartPoint = [number, number | null];

type ThresholdSeries = {
  id: string;
  name: string;
  type?: 'line';
  data: ChartPoint[];
};

type ThresholdExceededChartProps = {
  series: ThresholdSeries[];
  alarm?: {
    Created?: string | null;
  };
  isLoading?: boolean;
  threshold?: number;
};

function getChartLocales(language?: string) {
  if (language === 'dk') {
    return {
      luxonLocale: 'da',
      numberLocale: 'da-DK',
    };
  }

  return {
    luxonLocale: language || undefined,
    numberLocale: language || undefined,
  };
}

const buildCumulativeData = (data: ChartPoint[]): ChartPoint[] => {
  let runningTotal = 0;

  return data.map(([timestamp, value]) => {
    if (typeof value !== 'number') {
      return [timestamp, null];
    }

    runningTotal += value;
    return [timestamp, runningTotal];
  });
};

const ThresholdExceededChart = ({
  series,
  alarm,
  isLoading = false,
  threshold,
}: ThresholdExceededChartProps) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation('alarms');
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const isDark = theme.palette.mode === 'dark';
  const { luxonLocale } = getChartLocales(i18n.resolvedLanguage);

  const options = useMemo<Highcharts.Options>(
    () => ({
      lang: {
        noData: t('common.no_data_available'),
      },
      chart: {
        type: 'line',
        height: 420,
        backgroundColor: 'transparent',
        spacing: [20, 20, 24, 12],
      },
      credits: { enabled: false },
      title: { text: undefined },
      time: { useUTC: true },
      rangeSelector: { enabled: false },
      navigator: { enabled: false },
      scrollbar: { enabled: false },
      xAxis: {
        type: 'datetime',
        tickColor: isDark ? '#293140' : '#d6dfeb',
        lineColor: isDark ? '#293140' : '#d6dfeb',
        gridLineWidth: 1,
        gridLineColor: isDark
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(108, 124, 147, 0.2)',
        gridLineDashStyle: 'ShortDot',
        labels: {
          formatter: function formatter() {
            const value =
              typeof this.value === 'number' ? this.value : Number(this.value);

            return DateTime.fromMillis(value, { zone: 'utc' })
              .toLocal()
              .setLocale(luxonLocale)
              .toFormat('dd-LL HH:mm');
          },
          style: {
            color: isDark ? '#c7d1e0' : '#334155',
            fontSize: '11px',
          },
        },
        plotLines: alarm?.Created
          ? [
              {
                dashStyle: 'Dot',
                value: DateTime.fromISO(alarm.Created, {
                  zone: 'utc',
                }).toMillis(),
                width: 1,
                zIndex: 5,
                label: {
                  text: t('charts.alarm_triggered', {
                    defaultValue: 'Alarm Triggered',
                  }),
                  align: 'center',
                  verticalAlign: 'top',
                  y: -10,
                  style: {
                    color: isDark ? '#ffffff' : '#000000',
                    fontWeight: '600',
                  },
                },
              },
            ]
          : undefined,
      },
      yAxis: {
        opposite: false,
        title: {
          text: t('charts.value'),
          align: 'middle',
          x: 0,
          style: {
            color: isDark ? '#c7d1e0' : '#334155',
            fontSize: '12px',
            fontWeight: '500',
          },
        },
        tickColor: isDark ? '#293140' : '#d6dfeb',
        lineColor: isDark ? '#293140' : '#d6dfeb',
        gridLineWidth: 1,
        gridLineColor: isDark
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(108, 124, 147, 0.2)',
        gridLineDashStyle: 'ShortDot',
        labels: {
          align: 'left',
          x: 0,
          style: {
            color: isDark ? '#c7d1e0' : '#334155',
            fontSize: '11px',
          },
        },
        plotLines:
          typeof threshold === 'number' && Number.isFinite(threshold)
            ? [
                {
                  dashStyle: 'Dash',
                  value: threshold,
                  width: 1,
                  zIndex: 5,
                  label: {
                    text: `Threshold: ${threshold}`,
                    align: 'right',
                    style: {
                      color: isDark ? '#ffffff' : '#000000',
                    },
                  },
                },
              ]
            : undefined,
      },
      legend: {
        enabled: true,
        verticalAlign: 'bottom',
        itemDistance: 16,
        symbolRadius: 0,
        itemStyle: {
          color: isDark ? '#d7e0ef' : '#1f2937',
          fontWeight: '500',
        },
      },
      tooltip: {
        shared: true,
        split: false,
        useHTML: true,
        borderRadius: 10,
        shadow: false,
        backgroundColor: isDark
          ? 'rgba(13, 18, 28, 0.96)'
          : 'rgba(255, 255, 255, 0.96)',
        borderColor: isDark ? '#334155' : '#cbd5e1',
        style: {
          color: isDark ? '#f1f5f9' : '#0f172a',
        },
        formatter: function formatter() {
          const date = DateTime.fromMillis(this.x as number, { zone: 'utc' })
            .toLocal()
            .setLocale(luxonLocale)
            .toFormat('dd-LL-yyyy HH:mm');

          const points = this.points || (this.point ? [this.point] : []);

          const rows = points
            .map((point) => {
              const numericValue =
                typeof point.y === 'number' ? point.y.toFixed(2) : '-';
              return `<div style="display:flex;align-items:center;gap:8px;margin-top:4px;"><span style="color:${point.color}">●</span><span>${point.series.name}: <b>${numericValue}</b></span></div>`;
            })
            .join('');

          return `<div><div style="font-weight:600;margin-bottom:4px;">${date}</div>${rows}</div>`;
        },
      },
      plotOptions: {
        series: {
          states: {
            inactive: {
              opacity: 1,
            },
          },
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 4,
              },
            },
          },
          lineWidth: 1.5,
          linecap: 'round',
          animation: {
            duration: 250,
          },
        },
      },
      noData: {
        style: {
          fontWeight: '400',
          color: isDark ? '#9fb0c7' : '#64748b',
        },
      },
      loading: {
        labelStyle: {
          fontWeight: '400',
          color: isDark ? '#e2e8f0' : '#1e293b',
        },
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0)',
        },
      },
      series: series.flatMap((entry) => {
        const cumulativeData = buildCumulativeData(entry.data);

        return [
          {
            ...entry,
            name: `${entry.name} (${t('charts.original', { defaultValue: 'Original' })})`,
            type: 'line',
            turboThreshold: 0,
          },
          {
            id: `${entry.id}-cumulative`,
            name: `${entry.name} (${t('charts.cumulative', { defaultValue: 'Cumulative' })})`,
            type: 'line',
            data: cumulativeData,
            turboThreshold: 0,
            dashStyle: 'Dash',
            lineWidth: 1.5,
          },
        ];
      }),
    }),
    [t, isDark, alarm.Created, threshold, series, luxonLocale],
  );

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    if (isLoading) {
      chart.showLoading(t('common.loading_data'));
      return;
    }
    chart.hideLoading();
  }, [isLoading, t]);

  return (
    <Box sx={{ mt: 1 }}>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
        ref={chartRef}
      />
    </Box>
  );
};

export default ThresholdExceededChart;
